import { useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEconPermission } from '@entities/economic/types';
import { ECON_DICTIONARIES, type EconDictName } from '@entities/economic/dictionaryTypes';
import { TabPanel, Tabs, typo, type TabItem } from '@entities/fleet';
import { VatRatesView } from '@widgets/VatRates/VatRatesView';
import { DictPanel } from './DictPanel';
import { ProfitSplitForm } from './ProfitSplitForm';
import { Lead, PanelBox, Root } from './styles';
import { VAT_TAB } from './types';

type HandbookTab = EconDictName | typeof VAT_TAB;

const TABS: readonly TabItem<HandbookTab>[] = [
  ...ECON_DICTIONARIES.map((d) => ({ id: d.id, label: d.title })),
  { id: VAT_TAB, label: 'Ставки НДС' },
];

function parseTab(raw: string | null): HandbookTab {
  return TABS.find((t) => t.id === raw)?.id ?? 'objects';
}

/**
 * Справочники Экономиста: вкладка на справочник, выбранная — в адресе
 * (`?dict=licenses`), ссылку можно переслать. Заголовок раздела и плашку
 * «только чтение» рисует общий макет Экономиста.
 */
export function HandbooksView() {
  const uid = useId();
  const { can_write } = useEconPermission();
  const [params, setParams] = useSearchParams();
  const tab = parseTab(params.get('dict'));

  function onTab(next: HandbookTab) {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'objects') p.delete('dict');
        else p.set('dict', next);
        return p;
      },
      { replace: true },
    );
  }

  // У «Ставок НДС» пояснение своё: экран общий со Сметчиком.
  const description =
    tab === VAT_TAB ? undefined : ECON_DICTIONARIES.find((d) => d.id === tab)?.description;

  return (
    <Root>
      <Tabs
        items={TABS}
        value={tab}
        onChange={onTab}
        idPrefix={uid}
        label="Справочники Экономиста"
      />
      {description && <Lead>{typo(description)}</Lead>}
      <PanelBox>
        <TabPanel idPrefix={uid} id={tab}>
          {tab === VAT_TAB ? (
            <VatRatesView canWrite={can_write} />
          ) : tab === 'profit_split' ? (
            <ProfitSplitForm canWrite={can_write} />
          ) : (
            <DictPanel key={tab} dict={tab} canWrite={can_write} />
          )}
        </TabPanel>
      </PanelBox>
    </Root>
  );
}
